import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import useMigrateAllStacksVendor from "shared/Stack/useMigrateAllStacksVendor";
import { InputMaybe, StackVendor, StackVendorMigrationInput } from "types/generated";

import { GET_STACKS_REQUIRING_ATTENTION } from "./gql";

type MigrationArgs = {
  targetVendor: StackVendor;
  config?: InputMaybe<StackVendorMigrationInput>;
};

// Safety cap if BE keeps reporting left > 0. Sized for ~10k stacks at batch size 50.
const MAX_MIGRATION_ITERATIONS = 200;

const useStacksRequiringAttentionActions = () => {
  const { reportSuccess, onError } = useTypedContext(FlashContext);

  const [migrateAll, { loading: migrateLoading }] = useMigrateAllStacksVendor({
    refetchQueries: [GET_STACKS_REQUIRING_ATTENTION],
  });

  const [rollbackAll, { loading: rollbackLoading }] = useMigrateAllStacksVendor({
    refetchQueries: [GET_STACKS_REQUIRING_ATTENTION],
  });

  const runMigrationLoop = useCallback(
    async (mutate: typeof migrateAll, args: MigrationArgs, successMessage: string) => {
      try {
        let hasMore = true;
        let iterations = 0;

        while (hasMore) {
          if (++iterations > MAX_MIGRATION_ITERATIONS) {
            return;
          }

          const result = await mutate(args);

          if (!result?.data) {
            return;
          }

          const { migrated, left } = result.data.stackMigrateVendorAll;

          if (left > 0 && migrated === 0) {
            return;
          }

          hasMore = left > 0;
        }

        reportSuccess({ message: successMessage });
      } catch (error) {
        onError(error);
      }
    },
    [reportSuccess, onError]
  );

  const handleMigrateAll = useCallback(
    () =>
      runMigrationLoop(
        migrateAll,
        { targetVendor: StackVendor.Terragrunt },
        "All stacks have been migrated to Terragrunt"
      ),
    [runMigrationLoop, migrateAll]
  );

  const handleRollbackAll = useCallback(
    () =>
      runMigrationLoop(
        rollbackAll,
        {
          targetVendor: StackVendor.Terraform,
          config: { labels: ["terragrunt"], terragrunt: null },
        },
        "All stacks have been rolled back to Terraform"
      ),
    [runMigrationLoop, rollbackAll]
  );

  return {
    handleMigrateAll,
    handleRollbackAll,
    migrateLoading,
    rollbackLoading,
  };
};

export default useStacksRequiringAttentionActions;
